import {Alert, Badge, Card, CardBody, CardHeader, Col, Container, Row} from 'reactstrap';
import {useAuth} from '../contexts/AuthContext';
import {StorageHelper} from '../utils/storageHelper';
import {SetupRequirement} from "../api/api-client";

export const UserProfile = () => {
  const { 
    user, 
    userEmail,
    isAuthenticated, 
    masterKey, 
    publicKey,
    setupNeeded,
    isInitialized 
  } = useAuth();

  const getEncryptionStatus = () => {
    if ((setupNeeded & SetupRequirement.MasterKey) !== 0) return 'Not configured';
    if (!masterKey) return 'Keys not loaded';
    return 'Active';
  };

  const getEncryptionStatusColor = () => {
    const status = getEncryptionStatus();
    switch (status) {
      case 'Active': return 'success';
      case 'Keys not loaded': return 'warning';
      case 'Not configured': return 'danger';
      default: return 'secondary';
    }
  };

  const getTroubleshootingInfo = () => {
    const info = [];
    
    if (!isInitialized) {
      info.push('Auth system initializing...');
    } else if (!isAuthenticated) {
      info.push('User not authenticated');
    }
    
    const hasStoredToken = !!StorageHelper.getToken();
    const hasStoredPassphrase = !!StorageHelper.getPassphrase();
    
    if (!hasStoredToken) info.push('No stored token');
    if (!hasStoredPassphrase) info.push('No stored passphrase');
    
    if (setupNeeded === SetupRequirement.None && !masterKey) {
      info.push('Setup complete but master key not loaded');
    }
    
    return info.length > 0 ? info : ['All systems operational'];
  };

  const handleClearStorage = () => {
    if (window.confirm('Clear all stored data? You will need to log in again.')) {
      StorageHelper.clearAuthData();
      window.location.reload();
    }
  };

  if (!isInitialized) {
    return (
      <Container className="mt-4">
        <Row>
          <Col md={8} lg={6} className="mx-auto">
            <h2 className="mb-4">Profile Settings</h2>
            <Card>
              <CardBody>
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Initializing...</p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row>
        <Col md={10} lg={8} className="mx-auto">
          <h2 className="mb-4">Profile Settings</h2>
          
          {/* Basic Profile Information */}
          <Card className="mb-4">
            <CardHeader>
              <h5 className="mb-0">Profile Information</h5>
            </CardHeader>
            <CardBody>
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Email:</strong> {user?.email || userEmail || 'Not available'}
                </div>
                <div className="col-md-6">
                  <strong>User ID:</strong> {user?.id || 'Not available'}
                </div>
              </div>
              
              <Alert color="info" className="mt-4">
                <h6>Coming Soon</h6>
                <p className="mb-0">
                  Profile management features will include:
                </p>
                <ul className="mt-2 mb-0">
                  <li>Change password</li>
                  <li>Update display name</li>
                  <li>Manage authentication methods</li>
                  <li>Privacy settings</li>
                  <li>Notification preferences</li>
                </ul>
              </Alert>
            </CardBody>
          </Card>

          {/* Security & Status Information */}
          <Card className="mb-4">
            <CardHeader>
              <h5 className="mb-0">Security & Status</h5>
            </CardHeader>
            <CardBody>
              {/* Authentication Status */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Authentication:</strong>{' '}
                  <Badge color={isAuthenticated ? 'success' : 'danger'}>
                    {isAuthenticated ? 'Authenticated' : 'Not authenticated'}
                  </Badge>
                </div>
                <div className="col-md-6">
                  <strong>Setup Status:</strong>{' '}
                  <Badge color={setupNeeded === SetupRequirement.None ? 'success' : 'warning'}>
                    {setupNeeded === SetupRequirement.None ? "Complete!" : Object.values(SetupRequirement)
                       .filter(v => typeof v === 'number') // Only keep the numeric enum members
                       .filter(flag => (setupNeeded & (flag as number)) !== 0)
                       .map(flag => (SetupRequirement[flag as number] + " needed"))
                       .join(', ')}
                  </Badge>
                </div>
              </div>

              {/* Encryption Status */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Encryption:</strong>{' '}
                  <Badge color={getEncryptionStatusColor()}>
                    {getEncryptionStatus()}
                  </Badge>
                </div>
                <div className="col-md-6">
                  <strong>Keys Available:</strong>{' '}
                  <span className="text-muted">
                    Master: {masterKey ? '✓' : '✗'}, Public: {publicKey ? '✓' : '✗'}
                  </span>
                </div>
              </div>

              {/* Troubleshooting Section */}
              <div className="mt-4">
                <h6>System Status</h6>
                <ul className="list-unstyled small text-muted">
                  {getTroubleshootingInfo().map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="mt-3">
                <button 
                  className="btn btn-sm btn-outline-danger me-2"
                  onClick={handleClearStorage}
                >
                  Clear Storage
                </button>
                <button 
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => window.location.reload()}
                >
                  Refresh
                </button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};